/**
 * Coverage comparison helpers (QUOTE COMP: 03).
 * Maps GET …/comparison — no local taxonomy.
 */

import localService from "@/services/localService";
import { marketPillLabel } from "@/utils/quoteNormalization";
import { FAILED_LEG_DEFAULT_REASON, stripResubmitHint } from "@/constants/quoteConstants";
import { readableReason } from "@/helpers/humanText";
import { currentUserId } from "@/services/comparisonAdapter";
import { groupCarrierOptions, hasCarrierOption, pickOptionFields } from "@/utils/carrierOptionLabel";

/**
 * One label per carrier name, in order. A carrier with no name is "Carrier", and when
 * several have none they are numbered so a picker or a chip row can tell them apart.
 */
export function carrierLabels(names) {
  const list = Array.isArray(names) ? names : [];
  const nameless = list.filter((n) => !String(n || "").trim()).length;
  let seen = 0;
  return list.map((n) => {
    const name = String(n || "").trim();
    if (name) return name;
    seen += 1;
    return nameless > 1 ? `Carrier ${seen}` : "Carrier";
  });
}

export function readRailCollapsedPreference(defaultCollapsed = true) {
  const raw = localService.getItem("s2rCoverageComparisonRailCollapsed");
  if (raw === null || raw === undefined) return defaultCollapsed;
  return Boolean(raw);
}

export function writeRailCollapsedPreference(collapsed) {
  localService.setItem("s2rCoverageComparisonRailCollapsed", Boolean(collapsed));
}

/** Listed lowercase; matched with `i`. Sorted longest-first. */
const LEADING_CONNECTIVES_FOR_COMPARE = Object.freeze(
  "on the other hand|in addition|for example|for instance|furthermore|moreover|additionally|nevertheless|nonetheless|consequently|accordingly|regardless|similarly|likewise|meanwhile|otherwise|therefore|however|besides|instead|hence|thus|also"
    .split("|")
    .sort((a, b) => b.length - a.length)
);

/**
 * Leading connective + comma, or + space before an article ("also the …").
 * Does not strip "Also covered" (no comma / not followed by the|a|an).
 */
const LEADING_CONNECTIVE_RE = new RegExp(
  String.raw`^(${LEADING_CONNECTIVES_FOR_COMPARE.join("|")})\b(?:\s*,\s*|\s+(?=the\b|a\b|an\b))`,
  "i"
);

const COMPARE_PUNCTUATION_RE = /\p{P}/gu;
const FORMAT_CHARS_RE = /\p{Cf}/gu;
const HYPHEN_AS_SPACE_RE = /[\u2010-\u2015\u2212-]/g;

function protectNumericPunctuation(s) {
  return s
    .replaceAll(/(\d)\.(\d)/g, "$1\uE000$2")
    .replaceAll(/(\d),(\d)/g, "$1\uE001$2")
    .replaceAll("%", "\uE002");
}

function restoreNumericPunctuation(s) {
  return s.replaceAll("\uE000", ".").replaceAll("\uE001", "").replaceAll("\uE002", "%");
}

/** Compare-only normalize (S2CWB-344). Does not mutate display / export / API strings. */
export function normalizeTextForCompare(text) {
  let s = String(text).replaceAll(/\s+/g, " ").trim();
  if (!s) return "";
  s = s.replaceAll(FORMAT_CHARS_RE, "");
  s = s.replace(/^[^\p{L}\p{N}]+/u, "");
  let prev;
  do {
    prev = s;
    s = s.replace(LEADING_CONNECTIVE_RE, "");
  } while (s !== prev && s);
  s = s.replaceAll(HYPHEN_AS_SPACE_RE, " ");
  s = protectNumericPunctuation(s);
  s = s.replaceAll(COMPARE_PUNCTUATION_RE, "");
  s = restoreNumericPunctuation(s);
  return s.replaceAll(/\s+/g, " ").trim();
}

/** Prefer valueNumeric, else compare-normalized valueText (display text unchanged). */
export function cellCompareKey(cell) {
  if (!cell) return null;
  const numeric = cell.valueNumeric;
  if (numeric !== null && numeric !== undefined && numeric !== "") {
    const n = typeof numeric === "number" ? numeric : Number(numeric);
    if (!Number.isNaN(n)) return `n:${n}`;
  }
  const text = cell.valueText;
  if (text === null || text === undefined) return null;
  const normalized = normalizeTextForCompare(text);
  return normalized === "" ? null : `t:${normalized}`;
}

/** Broker override supplies the effective value (AC 4/6/8), extraction unchanged. */
export function cellHasBrokerOverride(cell) {
  const override = cell?.override;
  if (!override || typeof override !== "object") return false;
  const value = override.valueText;
  return value !== null && value !== undefined && String(value).trim() !== "";
}

/**
 * Cell states that say "we have no reading here" rather than anything about the
 * cover: the row could not be read, was never analysed for this quote, is out of
 * scope for it, or the quote's documents simply do not contain it (an uncited
 * absence, S2CWB-340). None of them is a value, and none of them is "not present".
 */
export const UNREAD_CELL_STATES = Object.freeze(["failed", "not_analysed", "not_applicable", "not_in_document"]);

/**
 * S2CWB-340: whether a cell names the document it was read from. It accepts a
 * positive page number, a document key or a form number, any one of them. Only a
 * cited absence may read "Not covered / not present"; an uncited one only says the
 * document does not contain the row. A citation is openable only with a
 * `documentKey`; without one the form/page is shown as plain text.
 */
export function cellHasCitedSource(cell) {
  if (!cell) return false;
  const present = (v) => v !== null && v !== undefined && String(v).trim() !== "";
  const page = present(cell.pageNumber) ? Number(cell.pageNumber) : NaN;
  if (Number.isInteger(page) && page > 0) return true;
  return present(cell.documentKey) || present(cell.documentNumber);
}

/**
 * S2CWB-414: the `source` of a cell the API built from the figures the underwriter sent
 * with a quote that has no document. Such a cell shows its value marked "From the
 * underwriter" and takes no part in variance or "Differences only".
 */
export const UNDERWRITER_SOURCE = "underwriter";

function isUnderwriterRaw(raw) {
  return Boolean(raw) && raw.source === UNDERWRITER_SOURCE;
}

function isUnreadCell(cell) {
  return UNREAD_CELL_STATES.includes(cell?.state) && !cellHasBrokerOverride(cell);
}

/**
 * Mark differing cells only. A clear majority marks the minority; with no clear
 * majority every compared cell is marked. Override values count even when
 * extraction failed.
 */
export function varyingQuoteIdsForRow(cellsByQuoteId) {
  const entries = Object.entries(cellsByQuoteId || {}).filter(([, cell]) => {
    if (!cell || cell.excludedFromVariance) return false;
    if (["pending", "not_started", "no_document", "underwriter"].includes(cell.state)) return false;
    if ((cell.state === "not_found" && !cellHasBrokerOverride(cell)) || isUnreadCell(cell)) {
      return false;
    }
    return cellCompareKey(cell) !== null;
  });
  if (entries.length < 2) return new Set();

  const keys = entries.map(([, cell]) => cellCompareKey(cell));
  if (new Set(keys).size <= 1) return new Set();

  const counts = {};
  for (const k of keys) counts[k] = (counts[k] || 0) + 1;

  let majorityKey = null;
  let majorityCount = 0;
  let ties = 0;
  for (const [k, c] of Object.entries(counts)) {
    if (c > majorityCount) {
      majorityCount = c;
      majorityKey = k;
      ties = 1;
    } else if (c === majorityCount) {
      ties += 1;
    }
  }

  // No clear majority (all distinct, or a tie, including two quotes that disagree):
  // there is no "usual" value to measure against, so every compared cell differs.
  // Measuring against the first column would leave column 1 unmarked (S2CWB-350).
  if (majorityCount === 1 || ties > 1) {
    return new Set(entries.map(([id]) => id));
  }
  return new Set(entries.filter(([, cell]) => cellCompareKey(cell) !== majorityKey).map(([id]) => id));
}

export function rowIsIdenticalAcrossQuotes(cellsByQuoteId, quoteIds) {
  const ids = (quoteIds || []).filter(Boolean);
  if (ids.length < 2) return true;
  const present = [];
  for (const qid of ids) {
    const cell = cellsByQuoteId?.[qid];
    // A column with no document is never compared: it neither differs nor agrees, so
    // one carrier that sent nothing cannot switch "Differences only" off for the matrix.
    // The underwriter's figures shown there take no part either (S2CWB-414).
    if (cell && (cell.state === "no_document" || cell.state === "underwriter")) continue;
    if (!cell || cell.state === "pending" || cell.state === "not_started") return false;
    // Unknown is not "the same": a row one quote could not read may differ there, so
    // "Differences only" must keep it on screen rather than hide it as identical.
    if (isUnreadCell(cell)) return false;
    if (cell.excludedFromVariance) continue;
    if (cell.state === "not_found" && !cellHasBrokerOverride(cell)) {
      present.push("not_found");
      continue;
    }
    const key = cellCompareKey(cell);
    present.push(key === null ? "empty" : key);
  }
  if (present.length < 2) return true;
  return new Set(present).size <= 1;
}

export function filterDifferencesOnly(sections, differencesOnly) {
  if (!differencesOnly) return { sections, hiddenCount: 0 };
  let hiddenCount = 0;
  const next = [];
  for (const section of sections || []) {
    const rows = [];
    for (const row of section.rows || []) {
      if (row.identicalAcrossQuotes) hiddenCount += 1;
      else rows.push(row);
    }
    if (rows.length) next.push({ ...section, rows });
  }
  return { sections: next, hiddenCount };
}

/** The `documentType` values a citation may carry (S2CWB-228 AC 8). */
const CITATION_DOCUMENT_TYPES = ["quote", "base_form", "binder"];

/**
 * The viewer kind for a cited `documentType`, or null for a type the viewer does
 * not open. It is a label for the viewer only: WHICH document opens is decided by
 * `documentKey` alone, and a kind is never used to pick "the policy PDF" instead.
 */
export function resolveCitationPdfKind(documentType) {
  return { quote: "quote", base_form: "policy", binder: "binder" }[documentType] || null;
}

function normalizeDocumentKey(value) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function normalizeDocumentType(value) {
  return CITATION_DOCUMENT_TYPES.includes(value) ? value : null;
}

function normalizeConfidenceSummary(value) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

/**
 * S2CWB-346: the competing values the API recovered from the stored reason, as
 * `{ values: [...], complete }`; null for any other cell. `complete` is true only
 * when the API says so; a malformed payload never breaks the cell.
 */
function normalizeConfidenceCandidates(value) {
  if (!value || typeof value !== "object" || !Array.isArray(value.values)) return null;
  const values = value.values.filter((item) => typeof item === "string" && item.trim() !== "");
  // A list we had to drop items from is no longer the whole list.
  return { values, complete: value.complete === true && values.length === value.values.length };
}

/** not_started | queued | running | complete | failed */
function normalizeAnalysisStatus(status) {
  if (status === null || status === undefined || status === "") return "not_started";
  const s = String(status).trim().toLowerCase();
  if (s === "queued" || s === "running" || s === "complete" || s === "failed" || s === "not_started") {
    return s;
  }
  return "not_started";
}

function isAnalysisInFlight(status) {
  return status === "queued" || status === "running";
}

function cellHasValue(raw) {
  if (!raw) return false;
  if (raw.valueText !== null && raw.valueText !== undefined && String(raw.valueText).trim() !== "") {
    return true;
  }
  return raw.valueNumeric !== null && raw.valueNumeric !== undefined && raw.valueNumeric !== "";
}

/**
 * Map run + extractionStatus → UI cell state. In-flight / failed run status wins (AC8).
 *
 * Only an explicit `not_found` reading that cites a document is "not_found" (S2CWB-228
 * AC 10, S2CWB-340). An uncited `not_found` — the pipeline's "no document returned
 * this wording" — is "not_in_document": absent from what we read, which is not a
 * finding that the cover lacks it. No cell at all — a row added to the taxonomy after
 * this quote was analysed — or a status this client does not know is "not_analysed":
 * nothing was read, which says nothing about the cover.
 */
export function cellStateFromRaw(
  rawCell,
  { analysisInFlight, analysisNotStarted, analysisNoDocument, analysisFailed } = {}
) {
  if (analysisInFlight) return "pending";
  if (analysisNoDocument) return "no_document";
  if (analysisNotStarted) return "not_started";
  if (analysisFailed) return "failed";
  if (!rawCell) return "not_analysed";

  const status = String(rawCell.extractionStatus || "")
    .trim()
    .toLowerCase();

  if (status === "failed") return "failed";
  if (status === "extracted") return "extracted";
  if (status === "not_found") {
    if (cellHasValue(rawCell)) return "extracted";
    return cellHasCitedSource(rawCell) ? "not_found" : "not_in_document";
  }
  if (status === "not_applicable") return "not_applicable";
  if (cellHasValue(rawCell)) return "extracted";
  return "not_analysed";
}

/**
 * S2CWB-236: the count of rows this quote's analysis could not read, exactly as the
 * backend published it (`quotes[].unresolvedRowCount`).
 *
 * Returns null — never 0 — for a payload that does not carry the key, or carries
 * something that is not a non-negative integer. 0 means "measured, and clean"; a
 * missing key means "not measured", and collapsing the two would let the matrix
 * assert a clean run on a payload that never reported one.
 */
export function normalizeUnresolvedRowCount(value) {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) return null;
  return value;
}

/**
 * Whole-number percent of a run in flight, from the API's `analysisProgress`
 * ({ done, total, percent }). null when the run has not reported, is not in flight, or the
 * numbers are unusable, so the column keeps its plain text instead of showing "NaN%".
 * Never 100: a column still saying "Analysing" at 100% looks stuck.
 */
export function analysisProgressPercent(progress, inFlight) {
  if (!inFlight || !progress || typeof progress !== "object") return null;
  const percent = Number(progress.percent);
  if (!Number.isFinite(percent) || percent < 0) return null;
  return Math.min(99, Math.floor(percent));
}

function isDeclinedCard(card) {
  return String(card?.rowStatus || "").toLowerCase() === "declined";
}

function isFailedCard(card) {
  return String(card?.rowStatus || "").toLowerCase() === "failed";
}

/** Why a cell with no stored reading cannot be overridden (also the API's not-found, said plainly). */
/**
 * Safety net for a lost `coverageAnalysisUpdated` event: while a column is analysing, the
 * page quietly re-reads the comparison on this interval, for at most this long per run.
 */
export const COMPARISON_POLL_INTERVAL_MS = 8000;
export const COMPARISON_POLL_MAX_MS = 10 * 60 * 1000;

export const NOTHING_TO_OVERRIDE = "Nothing was read for this carrier yet, so there is no reading to override.";

/** Whether the signed-in user owns this override; false when the payload cannot say. */
function overrideOwnedByCurrentUser(override) {
  if (typeof override.ownedByCurrentUser === "boolean") return override.ownedByCurrentUser;
  const owner = String(override.ownerId || "").trim();
  const me = String(currentUserId() || "").trim();
  return Boolean(owner) && Boolean(me) && owner === me;
}

function buildMappedCell(q, rowKey, rawIn) {
  // The underwriter's figure counts only in a column with no document, where nothing
  // was read; anywhere else the cell is as if absent (S2CWB-414).
  const fromUnderwriter =
    isUnderwriterRaw(rawIn) && Boolean(q.analysisNoDocument) && !q.failed && !q.declined && cellHasValue(rawIn);
  const raw = isUnderwriterRaw(rawIn) && !fromUnderwriter ? null : rawIn;
  // A failed leg is its own column state: it never reached underwriting, which is not a decline.
  const state = q.failed
    ? "leg_failed"
    : q.declined
    ? "declined"
    : fromUnderwriter
    ? "underwriter"
    : cellStateFromRaw(raw, {
        analysisInFlight: q.analysisInFlight,
        analysisNotStarted: q.analysisNotStarted,
        analysisNoDocument: q.analysisNoDocument,
        // A failed re-run that left earlier cells in place shows them as they are.
        analysisFailed: q.analysisFailed && !q.analysisFailedPreserved,
      });
  const override =
    raw?.override && typeof raw.override === "object"
      ? {
          valueText: raw.override.valueText ?? null,
          note: raw.override.note ?? null,
          changedById: raw.override.changedById ?? null,
          changedByName: raw.override.changedByName ?? null,
          changedOn: raw.override.changedOn ?? null,
          supersededExtraction: Boolean(raw.override.supersededExtraction),
          // Only the owner may edit or clear an override. The API says so with
          // `ownedByCurrentUser`, or names the owner with `ownerId`, which is compared
          // with the signed-in user. The server enforces the rule; the cell hides it.
          ownedByCurrentUser: overrideOwnedByCurrentUser(raw.override),
        }
      : null;
  const brokerEdited = cellHasBrokerOverride({ override });
  const hasStoredCell =
    Boolean(raw) &&
    !q.declined &&
    !q.failed &&
    !["pending", "not_started", "no_document", "declined", "underwriter"].includes(state);

  return {
    quoteId: q.quoteId,
    rowKey,
    state,
    // True only when a stored cell from the payload backs this one and the column is
    // in a state that reads cells. The override endpoint answers "not found" otherwise.
    hasStoredCell,
    regenerating: Boolean(q.analysisRefreshing),
    // Shown "From the underwriter": a figure UWB sent, not a document reading.
    fromUnderwriter,
    excludedFromVariance:
      Boolean(q.declined) ||
      state === "pending" ||
      state === "not_started" ||
      state === "no_document" ||
      state === "underwriter" ||
      (UNREAD_CELL_STATES.includes(state) && !brokerEdited),
    valueText: raw?.valueText ?? null,
    valueNumeric: raw?.valueNumeric ?? null,
    carrierTerm: raw?.carrierTerm ?? null,
    excerpt: raw?.excerpt ?? null,
    pageNumber: raw?.pageNumber ?? null,
    documentNumber: raw?.documentNumber ?? null,
    // The exact document the citation was read from; null when unknown, and then
    // a citation only opens a quote that has a single PDF (see CompareQuotesView).
    documentKey: normalizeDocumentKey(raw?.documentKey),
    documentType: normalizeDocumentType(raw?.documentType),
    extractionStatus: raw?.extractionStatus ?? null,
    confidence: typeof raw?.confidence === "string" ? raw.confidence : null,
    confidenceReason:
      typeof raw?.confidenceReason === "string" && raw.confidenceReason.trim() ? raw.confidenceReason.trim() : null,
    // What a low-confidence cell shows; screens never show confidenceReason (S2CWB-346).
    confidenceSummary: normalizeConfidenceSummary(raw?.confidenceSummary),
    confidenceCandidates: normalizeConfidenceCandidates(raw?.confidenceCandidates),
    extractedValueText: raw?.extractedValueText ?? null,
    override,
  };
}

/** AM Best's financial strength scale, longest code first so "A++" never reads as "A". */
const AM_BEST_RATINGS = "A++ A+ A- A B++ B+ B- B C++ C+ C- C D E F S NR".split(" ");
const AM_BEST_ALTERNATION = AM_BEST_RATINGS.map((r) => r.replace(/\+/g, "\\+")).join("|");
const AM_BEST_WHOLE = new RegExp(`^(?:${AM_BEST_ALTERNATION})$`, "i");
// Quoted in prose, between a matching pair of quotes that stand on their own, so the
// apostrophe in LLOYD'S never opens a quote: Lloyd's "A+" XV.
const AM_BEST_QUOTED = new RegExp(
  [
    ['"', '"'],
    ["'", "'"],
    ["“", "”"],
    ["‘", "’"],
  ]
    .map(([open, close]) => `(?:^|[^A-Za-z0-9])${open}\\s*(${AM_BEST_ALTERNATION})\\s*${close}(?![A-Za-z0-9])`)
    .join("|"),
  "i"
);
// A rating that carries + or -, standing on its own: "rated A- (Excellent)".
const AM_BEST_SIGNED = /(?:^|[^A-Za-z0-9+-])([ABC](?:\+\+|\+|-))(?![A-Za-z0-9+-])/i;
// Each rating's own AM Best descriptor. A rating followed by it in parentheses counts,
// "A (Excellent), Financial Size Category XII"; any other parenthetical does not,
// so "Schedule A (attached)" is not a rating.
const AM_BEST_DESCRIPTORS = {
  "A++": "superior",
  "A+": "superior",
  A: "excellent",
  "A-": "excellent",
  "B++": "good",
  "B+": "good",
  B: "fair",
  "B-": "fair",
  "C++": "marginal",
  "C+": "marginal",
  C: "weak",
  "C-": "weak",
  D: "poor",
  E: "under regulatory supervision",
  F: "in liquidation",
  S: "suspended",
  NR: "not rated",
};
const AM_BEST_WITH_PARENTHETICAL = new RegExp(
  `(?:^|[^A-Za-z0-9+-])(${AM_BEST_ALTERNATION})\\s*\\(\\s*([^()]*?)\\s*\\)`,
  "gi"
);

function amBestWithDescriptor(value) {
  for (const [, rating, descriptor] of value.matchAll(AM_BEST_WITH_PARENTHETICAL)) {
    const code = rating.toUpperCase();
    if (AM_BEST_DESCRIPTORS[code] === descriptor.toLowerCase().replace(/\s+/g, " ")) return code;
  }
  return "";
}

/**
 * An AM Best rating (A++ … F, S, NR) read from a cell's text, or "".
 * A value that is a rating shows as one. In older prose only a quoted rating or one
 * with + / - or followed by its own descriptor counts, so a stray capital letter never
 * becomes a rating, and the prose itself is never shown.
 */
export function amBestFromText(text) {
  const value = String(text ?? "").trim();
  if (!value) return "";
  if (AM_BEST_WHOLE.test(value)) return value.toUpperCase();
  const quoted = value.match(AM_BEST_QUOTED);
  if (quoted) return quoted.slice(1).find(Boolean).toUpperCase();
  const described = amBestWithDescriptor(value);
  if (described) return described;
  const signed = value.match(AM_BEST_SIGNED);
  return signed ? signed[1].toUpperCase() : "";
}

/**
 * The text a quote's cell shows, for its column header to use, or "" when
 * the cell shows no value. A broker override wins; otherwise only an extracted reading
 * counts. The cells a failed or in-flight re-run keeps on screen still count.
 */
function headerCellText(q, raw) {
  if (!raw || q.declined || q.failed) return "";
  const state = cellStateFromRaw(raw, {
    analysisInFlight: q.analysisInFlight && !q.analysisRefreshing,
    analysisNotStarted: q.analysisNotStarted,
    analysisNoDocument: q.analysisNoDocument,
    analysisFailed: q.analysisFailed && !q.analysisFailedPreserved,
  });
  if (state === "pending" || state === "not_started" || state === "no_document") return "";
  if (cellHasBrokerOverride(raw)) return String(raw.override.valueText).trim();
  if (state !== "extracted") return "";
  const text = raw.valueText;
  return text === null || text === undefined ? "" : String(text).trim();
}

/**
 * Fill what the quote leaves blank in its column header from its
 * `admitted_vs_surplus` and `am_best` cells, field by field. The quote's own market
 * and rating always win, so a reading never contradicts what UWB or the carrier said.
 */
function fillHeaderFromCells(q, cellsIndex) {
  if (!marketPillLabel(q.marketType, q.marketDisplay)) {
    const pill = marketPillLabel(null, headerCellText(q, cellsIndex.admitted_vs_surplus?.[q.quoteId]));
    if (pill) q.marketPillText = pill;
  }
  if (!q.amBestRating) {
    const rating = amBestFromText(headerCellText(q, cellsIndex.am_best?.[q.quoteId]));
    if (rating) q.amBestRating = rating;
  }
}

/** Map comparison payload → matrix view-model. */
export function mapComparisonPayloadToViewModel(payload, { cardQuotesById } = {}) {
  const sectionsIn = payload?.sections || [];
  const rowsIn = payload?.rows || [];
  const quotesIn = payload?.quotes || [];
  const cellsIn = payload?.cells || [];

  const mapped = quotesIn.map((q) => {
    const quoteId = q.quoteId;
    const analysisStatus = normalizeAnalysisStatus(q.analysisStatus);
    const card = cardQuotesById?.[quoteId] || null;
    // A failed leg never reached underwriting: like a declined one it has nothing to compare.
    const failed = isFailedCard(card);
    const declined = isDeclinedCard(card) || failed;
    const analysisInFlight = !declined && isAnalysisInFlight(analysisStatus);
    // Only cells read from a document: the underwriter's figures are not a run's output.
    const quoteHasCells = cellsIn.some((c) => (c.quoteId || c.quote_id) === quoteId && !isUnderwriterRaw(c));
    // No document to read wins over a failed run (S2CWB-414): a quote priced in UWB
    // without a PDF is waiting for its document, not broken. Only an explicit false,
    // and a failed run never over cells already published for the quote.
    const analysisNoDocument =
      !declined &&
      q.hasDocument === false &&
      (analysisStatus === "not_started" || (analysisStatus === "failed" && !quoteHasCells));
    const analysisFailed = !declined && analysisStatus === "failed" && !analysisNoDocument;
    return {
      quoteId,
      carrierName: q.carrierName || card?.carrier || "",
      // The card's ``market`` is a display label; the type comes from the quote row.
      marketType: q.marketType || card?.marketType || null,
      marketDisplay: q.marketDisplay || null,
      amBestRating: q.amBestRating || null,
      // The pill read from the cells when the quote has no market of its own.
      marketPillText: null,
      premiumAmount: q.premiumAmount ?? card?.premium ?? null,
      quoteRanOn: card?.quoteRanOn || null,
      documentRefs: q.documentRefs || [],
      analysisStatus,
      unresolvedRowCount: normalizeUnresolvedRowCount(q.unresolvedRowCount),
      analysisInFlight,
      analysisProgressPercent: analysisProgressPercent(q.analysisProgress, analysisInFlight),
      analysisRefreshing: analysisInFlight && quoteHasCells,
      // Only an explicit false counts: an API that does not send the flag keeps the
      // "awaiting analysis" reading, so the two sides can deploy in either order.
      hasDocument: typeof q.hasDocument === "boolean" ? q.hasDocument : null,
      analysisNoDocument,
      analysisNotStarted: !declined && analysisStatus === "not_started" && q.hasDocument !== false,
      analysisFailed,
      // One plain sentence from the API, only for a failed run: why it could not finish.
      analysisFailureReason:
        analysisFailed && typeof q.analysisFailureReason === "string" && q.analysisFailureReason.trim()
          ? q.analysisFailureReason.trim()
          : null,
      // The latest run failed, but the API kept the previous run's cells: they render
      // by their own status, under a column caveat, like `analysisRefreshing` does.
      analysisFailedPreserved: analysisFailed && quoteHasCells,
      declined,
      failed,
      // The matrix has no Resubmit action, so its reason never tells the broker to press one.
      failureReason: failed ? stripResubmitHint(readableReason(card?.declinationReason, FAILED_LEG_DEFAULT_REASON)) : "",
      cardQuote: card,
      // S2BWB-276: one of a carrier's several options (only then are these present).
      ...pickOptionFields(q),
      ...(hasCarrierOption(q) ? { carrierOrganizationId: q.carrierOrganizationId || null } : {}),
    };
  });
  // Columns keep the payload's order; a carrier's options sit next to each other.
  const quotes = groupCarrierOptions(mapped, (q) => q.carrierOrganizationId);

  const quoteIdsForVariance = quotes.filter((q) => !q.declined).map((q) => q.quoteId);
  const quoteIds = quotes.map((q) => q.quoteId);

  const cellsIndex = {};
  for (const c of cellsIn) {
    if (!c.quoteId || !c.rowKey) continue;
    if (!cellsIndex[c.rowKey]) cellsIndex[c.rowKey] = {};
    cellsIndex[c.rowKey][c.quoteId] = c;
  }
  for (const q of quotes) fillHeaderFromCells(q, cellsIndex);

  const sectionMeta = [...sectionsIn].sort(
    (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0) || String(a.code).localeCompare(String(b.code))
  );
  const sectionById = {};
  for (const s of sectionMeta) {
    if (s.id) sectionById[s.id] = s;
  }

  const rowsSorted = [...rowsIn].sort((a, b) => {
    const aOrd = sectionById[a.sectionId]?.displayOrder || 0;
    const bOrd = sectionById[b.sectionId]?.displayOrder || 0;
    if (aOrd !== bOrd) return aOrd - bOrd;
    return (a.displayOrder || 0) - (b.displayOrder || 0) || String(a.rowKey).localeCompare(String(b.rowKey));
  });

  const sections = sectionMeta.map((meta) => {
    const rawRows = rowsSorted.filter((r) => r.sectionId === meta.id || (meta.code && r.sectionCode === meta.code));
    const rows = rawRows.map((r) => {
      const rowKey = r.rowKey;
      const cellsByQuoteId = {};
      for (const q of quotes) {
        const raw = cellsIndex[rowKey]?.[q.quoteId] || null;
        cellsByQuoteId[q.quoteId] = buildMappedCell(q, rowKey, raw);
      }
      const varying = varyingQuoteIdsForRow(cellsByQuoteId);
      for (const qid of Object.keys(cellsByQuoteId)) {
        cellsByQuoteId[qid].varies = varying.has(qid);
      }
      return {
        rowKey,
        label: r.label,
        hint: r.hint,
        valueType: r.valueType,
        allowedValues: Array.isArray(r.allowedValues) ? r.allowedValues.filter(Boolean) : [],
        whyItMatters: r.whyItMatters,
        expandable: Boolean(r.expandable),
        displayOrder: r.displayOrder || 0,
        // S2CWB-233: rows whose values the clause deep dive shows beside this one.
        // Always an array, so an older payload without the key renders normally.
        relatedRowKeys: Array.isArray(r.relatedRowKeys) ? r.relatedRowKeys.filter(Boolean) : [],
        cellsByQuoteId,
        identicalAcrossQuotes: rowIsIdenticalAcrossQuotes(cellsByQuoteId, quoteIdsForVariance),
        varyingQuoteIds: [...varying],
      };
    });
    return {
      code: meta.code,
      title: meta.title || meta.code || "Section",
      note: meta.note || null,
      displayOrder: meta.displayOrder || 0,
      rows,
    };
  });

  return {
    submissionId: payload?.submissionId || null,
    organizationId: payload?.organizationId || null,
    quotes,
    quoteIds,
    sections,
  };
}

/* -------------------------------------------------------------------------
 * S2CWB-233 — clause deep dive helpers.
 *
 * Pure functions over the view-model above, so the deep dive reads exactly the
 * rows, cells and order the matrix renders.
 * ---------------------------------------------------------------------- */

/** `{ section, row }` for a row_key, or null when the view-model has no such row. */
export function findClause(viewModel, rowKey) {
  if (!viewModel || !rowKey) return null;
  for (const section of viewModel.sections || []) {
    const row = (section.rows || []).find((r) => r.rowKey === rowKey);
    if (row) return { section, row };
  }
  return null;
}

/**
 * The rows configured to travel with a clause (`relatedRowKeys`), in that order,
 * each tagged with its section title.
 *
 * Resolved against the UNFILTERED view-model: "Differences only" hides rows
 * that agree across quotes, but a waiting period that agrees still changes how
 * a BI definition reads. Keys the view-model does not hold, and the row itself,
 * are skipped rather than rendered as blanks.
 */
export function resolveRelatedRows(viewModel, row) {
  if (!row || !Array.isArray(row.relatedRowKeys)) return [];
  const related = [];
  for (const key of row.relatedRowKeys) {
    if (!key || key === row.rowKey || related.some((r) => r.rowKey === key)) continue;
    const found = findClause(viewModel, key);
    if (found) related.push({ ...found.row, sectionTitle: found.section.title });
  }
  return related;
}

/** row_keys of the expandable rows, in taxonomy order (section order, then row order). */
export function expandableRowSequence(sections) {
  const keys = [];
  for (const section of sections || []) {
    for (const row of section.rows || []) {
      if (row.expandable) keys.push(row.rowKey);
    }
  }
  return keys;
}

/**
 * The previous and next expandable rows around `rowKey`, for prev/next (AC 7).
 * `prev`/`next` are null at the ends, which is what disables the controls.
 */
export function clauseNeighbours(sections, rowKey) {
  const sequence = expandableRowSequence(sections);
  const index = sequence.indexOf(rowKey);
  return {
    prev: index > 0 ? sequence[index - 1] : null,
    next: index >= 0 && index < sequence.length - 1 ? sequence[index + 1] : null,
    index,
    total: sequence.length,
  };
}
